"use client"

import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
  Tooltip,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material"
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  PointOfSale as PointOfSaleIcon,
  Visibility as VisibilityIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material"

const VentaConfigPanel = ({
  // Usuario
  usuarioSeleccionado,
  setUsuarioSeleccionado,
  usuarios,
  // Resumen
  precioBaseTotal,
  precioTotal,
  aplicarDescuento,
  setAplicarDescuento,
  tempPorcentajeDescuento,
  setTempPorcentajeDescuento,
  applyDiscounts,
  limpiarDescuentosItems,
  montoTotalDescuentos,
  aplicarInteres,
  setAplicarInteres,
  tempPorcentajeInteres,
  setTempPorcentajeInteres,
  applyInterest,
  limpiarInteresesItems,
  montoTotalIntereses,
  aceptarImpuestos,
  checkboxFacturaNegro,
  impuestos,
  // Tipo de factura
  idTipoFactura,
  setIdTipoFactura,
  tiposFacturas,
  // Medios de pago
  selectedTipoMedioDePago,
  setSelectedTipoMedioDePago,
  tipoMedioDePago,
  selectedMedioDePago,
  setSelectedMedioDePago,
  medioDePagoIdTipoEspecifico,
  openCollapse,
  mostrarButtonModalMedioDePago,
  handleAbrirModalMedioDePago,
  // Acciones
  onClose,
  onGenerarVenta,
  isButtonDisabled,
  theme,
}) => {
  return (
    <Box
      sx={{
        width: { xs: "100vw", sm: 400 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
      }}
    >
      {/* Header del Drawer */}
      <Box
        sx={{
          p: 2,
          backgroundColor: "#5b5b5f",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Configuración de Venta
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Contenido scrolleable */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
        }}
      >
        {/* Tarjeta de Usuario */}
        <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
              <PersonIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
              Usuario
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              disablePortal
              options={usuarios.map((it) => ({
                label: `${it.usuario} ${it.nombre || ""} ${it.apellido || ""}`,
                nombre: it.nombre,
                apellido: it.apellido,
                id: it.id,
                usuario: it.usuario,
              }))}
              value={usuarioSeleccionado}
              onChange={(event, newValue) => setUsuarioSeleccionado(newValue)}
              renderInput={(params) => <TextField {...params} label="Usuario" />}
            />
          </CardContent>
        </Card>

        {/* Resumen de Venta */}
        <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
              <ReceiptIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
              Resumen de Venta
            </Typography>

            <Box
              sx={{
                backgroundColor: alpha(theme.palette.primary.light, 0.1),
                p: 2,
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(precioBaseTotal)}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={aplicarDescuento}
                    onChange={(e) => {
                      const isChecked = e.target.checked
                      setAplicarDescuento(isChecked)

                      // Limpiar descuentos al desmarcar
                      if (!isChecked) {
                        limpiarDescuentosItems()
                      }
                    }}
                    color="primary"
                  />
                }
                label="Aplicar Descuento"
              />

              <Collapse in={aplicarDescuento} timeout={300}>
                <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    label="Descuento en Porcentaje (%)"
                    type="number"
                    value={tempPorcentajeDescuento}
                    onChange={(e) => setTempPorcentajeDescuento(e.target.value)}
                    onBlur={applyDiscounts}
                    InputProps={{
                      inputProps: { step: 0.01 },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    fullWidth
                    size="small"
                  />
                  <Tooltip title="Limpiar todos los descuentos">
                    <IconButton
                      onClick={limpiarDescuentosItems}
                      color="primary"
                      size="small"
                      sx={{
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderRadius: 1,
                      }}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Collapse>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={aplicarInteres}
                    onChange={(e) => {
                      const isChecked = e.target.checked
                      setAplicarInteres(isChecked)
                      if (!isChecked) {
                        limpiarInteresesItems()
                      }
                    }}
                    color="primary"
                  />
                }
                label="Aplicar Interés"
              />

              <Collapse in={aplicarInteres} timeout={300}>
                <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    label="Interés en Porcentaje (%)"
                    type="number"
                    value={tempPorcentajeInteres}
                    onChange={(e) => setTempPorcentajeInteres(e.target.value)}
                    onBlur={applyInterest}
                    InputProps={{
                      inputProps: { step: 0.01 },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    fullWidth
                    size="small"
                  />
                  <Tooltip title="Limpiar todos los intereses">
                    <IconButton
                      onClick={limpiarInteresesItems}
                      color="primary"
                      size="small"
                      sx={{
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderRadius: 1,
                      }}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Collapse>

              {/* Mostrar Total Descuentos si hay descuentos */}
              {montoTotalDescuentos > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    color: "error.main",
                  }}
                >
                  <Typography variant="body2">Total Descuentos:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    -{new Intl.NumberFormat("es-AR", {
                      style: "currency",
                      currency: "ARS",
                    }).format(montoTotalDescuentos)}
                  </Typography>
                </Box>
              )}

              {montoTotalIntereses > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    color: "success.main",
                  }}
                >
                  <Typography variant="body2">Total Intereses:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    +{new Intl.NumberFormat("es-AR", {
                      style: "currency",
                      currency: "ARS",
                    }).format(montoTotalIntereses)}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {!aceptarImpuestos &&
                checkboxFacturaNegro == false &&
                impuestos &&
                Array.isArray(impuestos) &&
                impuestos.length > 0
                ? impuestos
                    .filter((impuesto) => !impuesto.eliminado && !impuesto.incluidoEnPrecio)
                    .map((impuesto, index) => {
                      const montoImpuesto = (precioBaseTotal * impuesto.porcentaje) / 100
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {impuesto.descripcion} ({impuesto.porcentaje}%):
                          </Typography>
                          <Typography variant="body2">
                            {new Intl.NumberFormat("es-AR", {
                              style: "currency",
                              currency: "ARS",
                            }).format(montoImpuesto)}
                          </Typography>
                        </Box>
                      )
                    })
                : null}

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1,
                }}
              >
                <Typography variant="h6">TOTAL:</Typography>
                <Typography variant="h6" color="rgb(244, 147, 14)" fontWeight="bold">
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(precioTotal)}
                </Typography>
              </Box>
            </Box>

            {checkboxFacturaNegro == false && (
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                options={tiposFacturas || []}
                getOptionLabel={(option) => {
                  if (!option || typeof option !== "object") return ""
                  return option.descripcion || ""
                }}
                value={idTipoFactura}
                onChange={(event, newValue) => {
                  setIdTipoFactura(newValue)
                }}
                renderInput={(params) => <TextField {...params} label="Tipo de factura" />}
              />
            )}
          </CardContent>
        </Card>

        {/* Medios de Pago */}
        <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
              <PaymentsIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
              Medios de Pago
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                options={tipoMedioDePago || []}
                getOptionLabel={(option) => {
                  if (!option || typeof option !== "object") return ""
                  return option.descripcion || ""
                }}
                value={selectedTipoMedioDePago}
                onChange={(event, newValue) => {
                  setSelectedTipoMedioDePago(newValue)
                }}
                renderInput={(params) => <TextField {...params} label="Tipo de medio de pago" />}
              />

              {mostrarButtonModalMedioDePago && (
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handleAbrirModalMedioDePago}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Ver medios de pago seleccionados
                </Button>
              )}

              <Collapse in={openCollapse}>
                <Box sx={{ mt: 2 }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disablePortal
                    options={medioDePagoIdTipoEspecifico || []}
                    getOptionLabel={(option) => {
                      if (!option || typeof option !== "object") return ""
                      return option.descripcion || ""
                    }}
                    value={selectedMedioDePago || null}
                    onChange={(event, newValue) => {
                      setSelectedMedioDePago(newValue)
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Medio de pago específico" />
                    )}
                  />
                </Box>
              </Collapse>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Footer fijo con botón */}
      <Box
        sx={{
          p: 2,
          backgroundColor: "white",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disabled={isButtonDisabled}
          onClick={onGenerarVenta}
          startIcon={<PointOfSaleIcon />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: "bold",
            boxShadow: 3,
            backgroundColor: "green",
            color: "white",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#2e7d32",
            },
            "&:disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.12)",
              color: "rgba(0, 0, 0, 0.26)",
            },
          }}
        >
          GENERAR VENTA
        </Button>
      </Box>
    </Box>
  )
}

export default VentaConfigPanel
